/**
 * utils/translations.js — Helpers para la edición de traducciones del catálogo
 * desde el panel admin (REQ-030 / DEC-023).
 *
 * El backend guarda las traducciones `en` en el JSONField `translations` de cada
 * modelo, con la forma `{ "en": { "<campo>": "<texto>" } }` (TASK-030). Los campos
 * nativos es-ES son la fuente de verdad y NO se tocan: el admin edita el overlay
 * `en` por separado y lo envía en el payload como `translations`.
 *
 * Estas funciones son puras y no dependen de Vue: sirven tanto dentro de
 * componentes (sobre objetos reactivos) como en services.
 */

export const TRANSLATION_LANG = 'en';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Asegura que `obj.translations` sea un objeto plano (mutable, sin claves colgadas). */
export function normalizeTranslations(obj, lang = TRANSLATION_LANG) {
  if (!isPlainObject(obj?.translations)) {
    obj.translations = {};
  }
  if (!isPlainObject(obj.translations[lang])) {
    obj.translations[lang] = {};
  }
  return obj.translations;
}

/** Devuelve la traducción `lang` del campo `field` (string, '' si no existe). */
export function getTranslation(obj, field, lang = TRANSLATION_LANG) {
  const entry = obj?.translations?.[lang];
  const value = entry?.[field];
  return value == null ? '' : String(value);
}

/** Fija (o limpia) la traducción `lang` del campo `field` en `obj.translations`. */
export function setTranslation(obj, field, value, lang = TRANSLATION_LANG) {
  const translations = isPlainObject(obj?.translations) ? { ...obj.translations } : {};
  const entry = isPlainObject(translations[lang]) ? { ...translations[lang] } : {};
  const clean = value == null ? '' : String(value).trim();
  if (clean === '') {
    delete entry[field];
  } else {
    entry[field] = clean;
  }
  if (Object.keys(entry).length) {
    translations[lang] = entry;
  } else {
    delete translations[lang];
  }
  obj.translations = translations;
  return obj.translations;
}

/** Indica si hay al menos una traducción `en` cargada para el objeto. */
export function hasTranslations(obj, lang = TRANSLATION_LANG) {
  const entry = obj?.translations?.[lang];
  return isPlainObject(entry) && Object.keys(entry).length > 0;
}

/**
 * Construye el fragmento de payload `translations` para guardar.
 * Devuelve `{ en: {...} }` solo si hay contenido; `undefined` si no (para omitirlo).
 */
export function buildTranslationsPayload(obj, lang = TRANSLATION_LANG) {
  const entry = obj?.translations?.[lang];
  if (!isPlainObject(entry) || !Object.keys(entry).length) return undefined;
  return { [lang]: { ...entry } };
}

/**
 * Nombre visible de una categoría (o subcategoría) según el idioma activo.
 *
 * Las categorías son DATOS del catálogo: su nombre nativo es-ES (`name`) es la
 * fuente de verdad y se usa SIEMPRE como valor de filtro/href (el backend
 * matchea `categories__name__iexact` contra el nombre ES). El overlay
 * `translations.en.name` es solo display. Si no hay traducción EN, cae al
 * nombre nativo.
 *
 * Acepta tanto objetos de `/api/categories/` (CategorySerializer) como los
 * dicts de `/api/products/filter-facets/` y `/api/categories/hierarchical/`
 * (que exponen `translations` con la misma forma).
 */
const DEFAULT_CATALOG_LOCALE = 'es';

export function localizedCategoryName(cat, locale = DEFAULT_CATALOG_LOCALE) {
  const native = cat?.name ?? '';
  if (String(locale || '').toLowerCase() === 'en') {
    const enName = cat?.translations?.en?.name;
    if (enName != null && String(enName).trim() !== '') {
      return String(enName).trim();
    }
  }
  return native;
}
